import { useState, useEffect } from 'react';
import { Box, Button, Fade, Grid, Stack, Typography } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import usePlanStore from '../../../../../store/planStore';
import CampaignNameInput from './CampaignNameInput/CampaignNameInput';
import DivisionSelect from './DivisionSelect/DivisionSelect';
import CategorySelect from './CategorySelect/CategorySelect';
import ClearFormDialog from './ClearFormDialog/ClearFormDialog';
import BrandSelect from './BrandSelect/BrandSelect';
import RevertChangesDialog from './RevertChangesDialog/RevertChangesDialog';
import ProceedChangesDialog from './ProceedChangesDialog/ProceedChangesDialog';
import useSingleMediaPlan from '../../hooks/useSingleMediaPlan';
import EditInfoBox from './EditInfoBox/EditInfoBox';
import useMetaPredictionsValidity from '../PlanSummary/OverridingBookingFlow/hooks/useMetaPredictionsValidity';
import MetaBookingModal from '../../MetaBooking/modals/MetaBookingModal';
import useMetaPredictionStatuses from '../PlanSummary/OverridingBookingFlow/hooks/useMetaPredictionStatuses';

export default function GeneralInfo() {
  const { t } = useTranslation();
  const {
    isGeneralInfoFilled,
    setIsGeneralInfoFilled,
    setActiveTab,
    generalInfoState,
    resetGeneralInfoState,
    savedGeneralInfoState,
    saveGeneralInfoState,
    resetCountryForms,
    revertGeneralInfoState,
    setSelectedCountry,
    resetAllSavedCountryForms,
    countryForms,
  } = usePlanStore();
  const [revertChangesDialogOpen, setRevertChangesDialogOpen] = useState(false);
  const [clearFormDialogOpen, setClearFormDialogOpen] = useState(false);
  const [proceedChangesDialogOpen, setProceedChangesDialogOpen] =
    useState(false);
  const [metaValidityDialogOpen, setMetaValidityDialogOpen] = useState(false);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: generalInfoState,
  });

  const { reset } = methods;

  useEffect(() => {
    setIsGeneralInfoFilled(
      !!(
        generalInfoState.division &&
        generalInfoState.brand &&
        generalInfoState.category &&
        generalInfoState.campaignName
      )
    );
  }, [setIsGeneralInfoFilled, generalInfoState]);

  const handleRevertFormSuccess = () => {
    revertGeneralInfoState();
    setRevertChangesDialogOpen(false);
  };

  const handleClearFormSuccess = () => {
    resetGeneralInfoState();
    resetCountryForms();
    setSelectedCountry(null);
    resetAllSavedCountryForms();
    setClearFormDialogOpen(false);
  };

  const handleOkButtonClick = () => {
    if (
      !savedGeneralInfoState ||
      (savedGeneralInfoState.division === generalInfoState.division &&
        savedGeneralInfoState.brand === generalInfoState.brand &&
        savedGeneralInfoState.category === generalInfoState.category)
    ) {
      saveGeneralInfoState();
      setActiveTab('countries');
    } else {
      setProceedChangesDialogOpen(true);
    }
  };

  const { id } = useParams();

  const handleProceedChangesSuccess = () => {
    saveGeneralInfoState();
    resetCountryForms();
    setActiveTab('countries');
    setSelectedCountry(null);
    setProceedChangesDialogOpen(false);
    resetAllSavedCountryForms();
  };
  const {
    data: pricingData,
    getMetaPredictions,
    asyncGetMetaPredictions,
    isLoading,
  } = useMetaPredictionsValidity(setMetaValidityDialogOpen);

  const {
    data: metaPredictionStatuses,
    getMetaPredictionStatuses,
    isLoading: isMetaPredictionsLoading,
  } = useMetaPredictionStatuses();

  const { fetchMediaPlan } = useSingleMediaPlan(
    asyncGetMetaPredictions,
    getMetaPredictionStatuses
  );

  useEffect(() => {
    if (id && !savedGeneralInfoState?.campaignName) {
      fetchMediaPlan(id);
    }
  }, []);

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        sx={{
          mt: '17px',
          p: '40px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          background: 'white',
        }}
      >
        {!!id && <EditInfoBox />}
        <Typography
          variant="h6"
          fontWeight={700}
          fontSize="14px"
          gutterBottom
          color="#8043B9"
          textTransform="uppercase"
        >
          {t('mediaPlanning.generalInfo')}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CampaignNameInput />
          </Grid>

          <Grid item xs={2.4}>
            <DivisionSelect />
          </Grid>
          <Grid item xs={2.4}>
            <BrandSelect />
          </Grid>
          <Grid item xs={2.4}>
            <CategorySelect />
          </Grid>

          {/* Buttons */}
          <Grid item xs={8} />
          <Grid
            item
            xs={4}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Stack flexDirection="row" gap="32px">
              <Fade
                in={
                  !!(
                    savedGeneralInfoState &&
                    (savedGeneralInfoState.division !==
                      generalInfoState.division ||
                      savedGeneralInfoState.brand !== generalInfoState.brand ||
                      savedGeneralInfoState.category !==
                        generalInfoState.category)
                  )
                }
                timeout={500}
                unmountOnExit
              >
                <Button
                  variant="text"
                  color="primary"
                  sx={{ fontWeight: 700 }}
                  onClick={() => setRevertChangesDialogOpen(true)}
                  disabled={!!id}
                >
                  Revert changes
                </Button>
              </Fade>
              <Button
                variant="outlined"
                color="primary"
                sx={{
                  fontWeight: 700,
                  borderRadius: '20px',
                  minWidth: '116px',
                }}
                onClick={() => setClearFormDialogOpen(true)}
                disabled={!!id}
              >
                Clear form
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOkButtonClick}
                sx={{ padding: '8px 24px', minWidth: '116px' }}
                disabled={!isGeneralInfoFilled || !!id}
              >
                {t('OK')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
        <RevertChangesDialog
          open={revertChangesDialogOpen}
          onClose={() => setRevertChangesDialogOpen(false)}
          onSuccess={handleRevertFormSuccess}
        />
        <ClearFormDialog
          open={clearFormDialogOpen}
          onClose={() => setClearFormDialogOpen(false)}
          onSuccess={handleClearFormSuccess}
        />
        <ProceedChangesDialog
          open={proceedChangesDialogOpen}
          onClose={() => setProceedChangesDialogOpen(false)}
          onSuccess={handleProceedChangesSuccess}
        />
        <MetaBookingModal
          open={metaValidityDialogOpen}
          onClose={() => setMetaValidityDialogOpen(false)}
          onSuccess={() => setMetaValidityDialogOpen(false)}
          header={t('mediaPlanning.metaBooking.modals.validityCheck.header')}
          text={t(
            'mediaPlanning.metaBooking.modals.validityCheck.text.hasBeenUpdated'
          )}
          isValidityCheck
          pricingData={pricingData}
          isInformationalOnly
          isLoading={isLoading}
        />
      </Box>
    </FormProvider>
  );
}
